export interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  categorySlug: string;
  image: string;
  author: string;
  date: string;
  readTime: string;
  content: ArticleSection[];
  tags: string[];
  featured: boolean;
}

export interface ArticleSection {
  type: 'heading' | 'paragraph' | 'image' | 'tip' | 'list';
  content: string;
  items?: string[];
  imageUrl?: string;
  imageAlt?: string;
}

export const articles: Article[] = [
  {
    id: '1',
    slug: 'basic-composition-guide',
    title: '亚洲自拍基础构图指南：10个必学技巧',
    excerpt: '掌握这些简单却效果显著的构图技巧，让你的自拍瞬间提升到专业水准。从三分法到引导线，全方位解析最适合亚洲面孔的构图方法。',
    category: '拍摄技巧',
    categorySlug: 'shooting-tips',
    image: 'https://readdy.ai/api/search-image?query=Asian%20woman%20demonstrating%20selfie%20composition%20techniques%20with%20smartphone%2C%20rule%20of%20thirds%20grid%20overlay%2C%20warm%20natural%20light%2C%20minimal%20cream%20wall%20background%2C%20editorial%20educational%20photography%2C%20clean%20aesthetic%2C%20professional%20tutorial%20style&width=1200&height=675&seq=article-composition&orientation=landscape',
    author: '小美',
    date: '2026-08-10',
    readTime: '8分钟',
    tags: ['构图技巧', '自拍入门', '三分法', '拍摄基础'],
    featured: true,
    content: [
      { type: 'heading', content: '引言' },
      { type: 'paragraph', content: '在亚洲自拍文化中，构图是决定一张自拍是否好看的首要因素。无论你使用什么设备、选择什么滤镜，如果基础构图不过关，自拍效果都会大打折扣。本文将为你详细拆解10个最适合亚洲面孔的自拍构图技巧，帮助你快速提升自拍水平。' },
      { type: 'heading', content: '三分法构图——最经典的自拍法则' },
      { type: 'paragraph', content: '三分法（Rule of Thirds）是摄影中最基础也最实用的构图法则。将画面用两条横线和两条竖线分成九个等份，将主体的关键部位（如眼睛）放置在交叉点上，可以让自拍看起来更加平衡和谐。对于亚洲自拍来说，通常将眼睛放在上方三分之一处效果最佳。' },
      { type: 'image', content: '', imageUrl: 'https://readdy.ai/api/search-image?query=Smartphone%20selfie%20demonstration%20with%20rule%20of%20thirds%20grid%20visible%2C%20Asian%20woman%20portrait%2C%20warm%20lighting%2C%20minimal%20interior%2C%20educational%20photography%20tutorial%2C%20clean%20cream%20background%2C%20professional%20composition&width=1200&height=675&seq=article-comp-2&orientation=landscape', imageAlt: '三分法自拍构图示范' },
      { type: 'heading', content: '中央对称——凸显面部特征的利器' },
      { type: 'paragraph', content: '中央对称构图非常适合展示端正的五官。将面部置于画面正中央，保持左右对称，可以营造出一种庄重而精致的感觉。这种构图方式在亚洲美妆自拍中尤其流行，因为它能最大程度地展现妆容细节和面部轮廓。' },
      { type: 'tip', content: '专业建议：使用中央对称构图时，确保背景足够简洁，避免干扰主体。浅色墙面或模糊化的自然背景都是绝佳选择。' },
      { type: 'heading', content: '引导线——让目光自然聚焦' },
      { type: 'paragraph', content: '利用环境中的线条元素（如栏杆、墙壁边缘、道路等）引导观者的视线向你的面部汇聚。这种技巧能创造出强烈的视觉张力和空间纵深感，让你的自拍在众多照片中脱颖而出。' },
      { type: 'heading', content: '负空间——留白的艺术' },
      { type: 'paragraph', content: '在自拍中适当留白，不仅能突出主体，还能营造出高级感。亚洲网红自拍风格中，大量使用负空间构图来传达简约、时尚的视觉语言。一般建议将人物放置在画面的一侧，另一侧留出足够的空白区域。' },
      { type: 'list', content: '更多构图技巧速查', items: ['对角线构图：将身体沿对角线放置，拉长身形', '框架构图：利用门窗等自然框架框住主体', '俯瞰角度：45度俯拍，显脸小效果最佳', '前景遮挡：利用花草等前景增加层次感', '动态构图：微微歪头或侧身，打破僵硬感'] },
      { type: 'heading', content: '总结' },
      { type: 'paragraph', content: '掌握这些构图技巧后，关键是多加练习。每张自拍前花几秒钟思考一下构图方案，你会发现自拍质量的提升是立竿见影的。记住，最好的构图不是死记硬背规则，而是在理解规则的基础上找到最适合自己的风格。' },
    ],
  },
  {
    id: '2',
    slug: 'korean-filter-guide',
    title: '韩国流行滤镜全解析：打造韩系高级感自拍',
    excerpt: '深度解析2026年最热门的韩国风滤镜搭配技巧，从清透水光感到复古胶片风，教你用手机轻松调出韩系高级感自拍。',
    category: '滤镜与风格',
    categorySlug: 'filter-styles',
    image: 'https://readdy.ai/api/search-image?query=Korean%20style%20photo%20editing%20before%20and%20after%20comparison%20on%20smartphone%2C%20soft%20dewy%20skin%20effect%2C%20warm%20pastel%20color%20tone%2C%20minimal%20clean%20aesthetic%2C%20beauty%20portrait%2C%20editorial%20photography%2C%20cream%20and%20peach%20palette&width=1200&height=675&seq=article-korean-filter&orientation=landscape',
    author: '菲菲',
    date: '2026-08-08',
    readTime: '10分钟',
    tags: ['韩系滤镜', '调色教程', '滤镜推荐', '美颜效果'],
    featured: true,
    content: [
      { type: 'heading', content: '韩系滤镜的魅力' },
      { type: 'paragraph', content: '韩系自拍风格以其清透自然、柔美高级的特点风靡亚洲。韩国滤镜的核心在于营造"水光肌"般的通透感和柔和的整体色调，让人看起来气色红润、皮肤细腻。本文将为你全面解析当下最流行的韩系滤镜及其搭配技巧。' },
      { type: 'heading', content: '清透水光感滤镜组合' },
      { type: 'paragraph', content: '清透水光感是韩系自拍最标志性的风格。核心参数：亮度+15、对比度-10、饱和度-5、色温偏暖+8。这种参数组合能够让皮肤呈现出柔和的珍珠光泽，同时保持画面的自然感。推荐使用Snapseed和Lightroom Mobile来实现这种效果。' },
      { type: 'image', content: '', imageUrl: 'https://readdy.ai/api/search-image?query=Dewy%20glass%20skin%20Asian%20beauty%20portrait%20selfie%2C%20soft%20natural%20lighting%2C%20minimal%20makeup%2C%20clear%20radiant%20complexion%2C%20warm%20neutral%20tones%2C%20editorial%20beauty%20photography%2C%20clean%20cream%20background&width=1200&height=675&seq=article-kf-2&orientation=landscape', imageAlt: '韩系清透水光感自拍效果' },
      { type: 'heading', content: '复古胶片风滤镜' },
      { type: 'paragraph', content: '复古胶片风最近在韩国社交平台上大热。通过增加颗粒感、降低清晰度、添加暖色调偏色，可以营造出90年代胶片相机的怀旧质感。推荐使用VSCO的C系列滤镜或Huji Cam应用来快速实现。' },
      { type: 'tip', content: '小贴士：拍摄复古胶片风自拍时，选择暖色灯光或傍晚的自然光效果会更好。适当添加暗角效果能够增强复古氛围。' },
      { type: 'heading', content: '奶茶色调滤镜' },
      { type: 'paragraph', content: '奶茶色调是2026年最具人气的亚洲自拍滤镜风格之一。它以温暖的棕褐色调为基底，让整体画面看起来像一杯浓郁的奶茶般温暖柔和。具体参数：色温+12、色调+5、高光偏暖黄、阴影偏棕。' },
      { type: 'list', content: '推荐的滤镜App排行', items: ['Lightroom Mobile：专业级调色，自定义程度最高', 'VSCO：丰富的预设滤镜库，胶片质感出众', 'Snapseed：谷歌出品，局部调整功能强大', '美图秀秀：亚洲用户友好，一键美颜便捷', 'Foodie：虽为美食App，但滤镜色调很适合亚洲肤色'] },
      { type: 'heading', content: '总结' },
      { type: 'paragraph', content: '韩系滤镜的核心在于"少即是多"。不要过度调色，保持画面的自然感和通透感才是关键。建议先拍好基础照片，再适度添加滤镜，这样才能打造出真正令人惊艳的韩系高级感自拍。' },
    ],
  },
  {
    id: '3',
    slug: 'best-selfie-phones-2026',
    title: '2026最佳自拍手机推荐：从入门到旗舰全方位横评',
    excerpt: '为你精选2026年自拍效果最好的智能手机，从千元机到高端旗舰，覆盖各种预算。详细对比前置摄像头参数、美颜算法与实拍样张。',
    category: '设备与灯光',
    categorySlug: 'equipment-lighting',
    image: 'https://readdy.ai/api/search-image?query=Multiple%20latest%20smartphones%20displayed%20on%20minimal%20marble%20surface%2C%20front%20camera%20visible%2C%20warm%20studio%20lighting%2C%20clean%20product%20photography%2C%20cream%20background%2C%20tech%20lifestyle%20aesthetic%2C%20professional%20review%20setup&width=1200&height=675&seq=article-phones&orientation=landscape',
    author: '科技阿杰',
    date: '2026-08-05',
    readTime: '12分钟',
    tags: ['自拍手机', '设备推荐', '手机横评', '前置摄像头'],
    featured: true,
    content: [
      { type: 'heading', content: '自拍手机怎么选？' },
      { type: 'paragraph', content: '在亚洲自拍文化中，一台好手机是拍出优质自拍的基础。2026年的智能手机在前置摄像头技术上有了重大突破，从更高像素到更智能的美颜算法，选择一台适合自己的自拍手机比以往更加重要。本文将为你横评各价位的自拍手机。' },
      { type: 'heading', content: '旗舰之选：自拍效果最强者' },
      { type: 'paragraph', content: '在旗舰机型中，前置摄像头像素普遍达到了5000万以上，配合大尺寸传感器和AI美颜芯片，能够呈现出令人惊艳的自拍效果。在光线充足的环境下，旗舰机的前置摄像头表现几乎可以媲美后置主摄。' },
      { type: 'image', content: '', imageUrl: 'https://readdy.ai/api/search-image?query=Smartphone%20selfie%20camera%20closeup%20with%20beauty%20mode%20interface%20displayed%2C%20Asian%20model%20on%20screen%2C%20warm%20lighting%2C%20minimal%20clean%20setup%2C%20tech%20product%20photography%2C%20cream%20and%20white%20background&width=1200&height=675&seq=article-ph-2&orientation=landscape', imageAlt: '旗舰手机前置摄像头特写' },
      { type: 'heading', content: '性价比之选：中端机型惊喜表现' },
      { type: 'paragraph', content: '3000-5000元价位段的中端机型在2026年有了质的飞跃。许多品牌将旗舰级的自拍算法下放到中端产品线，这意味着你不需要花大价钱也能拍出高质量的自拍。' },
      { type: 'tip', content: '选购技巧：关注前置摄像头的光圈大小（f/2.0以下更佳）和是否支持自动对焦。大光圈能带来更好的背景虚化效果和暗光表现。' },
      { type: 'list', content: '2026年自拍手机推荐榜单', items: ['旗舰级：前置5000万像素 + AI美颜芯片 + 4K视频录制', '高端级：前置3200万像素 + 超广角自拍 + 夜景模式', '中端级：前置2000万像素 + 智能HDR + 人像模式', '入门级：前置1600万像素 + 基础美颜 + 屏幕补光'] },
      { type: 'heading', content: '总结' },
      { type: 'paragraph', content: '选择自拍手机时，不要只看像素数字。传感器尺寸、光圈大小、美颜算法的自然度以及暗光表现同样重要。建议在购买前查看真实用户的实拍样张，而不是仅依赖官方样片来做判断。' },
    ],
  },
  {
    id: '4',
    slug: 'natural-light-selfie',
    title: '自然光自拍秘籍：一天中最佳拍摄时间全攻略',
    excerpt: '学会利用自然光拍摄，让自拍告别僵硬和过度美颜。从黄金时段到阴天柔光，详细讲解不同光线条件下的自拍策略。',
    category: '设备与灯光',
    categorySlug: 'equipment-lighting',
    image: 'https://readdy.ai/api/search-image?query=Asian%20woman%20taking%20selfie%20near%20large%20window%20with%20soft%20natural%20sunlight%2C%20golden%20hour%20warm%20glow%2C%20minimal%20interior%2C%20cream%20curtains%2C%20editorial%20lifestyle%20photography%2C%20peaceful%20morning%20vibe&width=1200&height=675&seq=article-natural-light&orientation=landscape',
    author: '小美',
    date: '2026-08-03',
    readTime: '7分钟',
    tags: ['自然光', '拍摄时间', '光线技巧', '打光方法'],
    featured: false,
    content: [
      { type: 'heading', content: '光线是自拍的灵魂' },
      { type: 'paragraph', content: '无论你的自拍设备多么先进，光线始终是决定自拍质量最关键的因素。学会利用自然光，你就能告别依赖滤镜和美颜的尴尬，拍出真正自然好看的自拍照。' },
      { type: 'heading', content: '黄金时段：日出后与日落前' },
      { type: 'paragraph', content: '摄影界公认的"黄金时段"指的是日出后一小时和日落前一小时。这时的阳光色温偏暖、光线柔和、角度低，能为你的自拍增添一层天然的柔光滤镜。在亚洲自拍风格中，黄昏时分的暖金色光芒尤其受欢迎，能够让皮肤呈现出健康自然的光泽。' },
      { type: 'image', content: '', imageUrl: 'https://readdy.ai/api/search-image?query=Golden%20hour%20selfie%20portrait%20of%20Asian%20woman%20outdoors%2C%20warm%20sunset%20backlight%2C%20soft%20rim%20light%20on%20hair%2C%20natural%20bokeh%20background%2C%20editorial%20fashion%20photography%2C%20dreamy%20atmosphere%2C%20cream%20and%20gold%20tones&width=1200&height=675&seq=article-nl-2&orientation=landscape', imageAlt: '黄金时段户外自拍示范' },
      { type: 'heading', content: '阴天：天然柔光箱' },
      { type: 'paragraph', content: '很多人觉得阴天不适合自拍，这其实是一个常见的误区。阴天的云层就像一个巨大的柔光箱，将阳光均匀散射，完全没有强烈阴影的困扰。在阴天自拍，皮肤会呈现出最柔和细腻的质感，非常适合亚洲美妆自拍。' },
      { type: 'tip', content: '实用技巧：阴天自拍时面朝天空方向，让均匀的散射光从正面照亮面部。适当提高曝光补偿+0.3到+0.7，可以让肤色更加明亮。' },
      { type: 'list', content: '一天中不同时间段的自拍建议', items: ['清晨6-8点：柔和的冷调光线，适合清新风格', '上午9-11点：明亮充足的日光，适合展示细节', '正午12-14点：光线最强，避免顶光直射面部', '下午15-17点：光线开始变暖，适合温馨风格', '黄昏17-19点：黄金时段，最出片的拍摄时间'] },
    ],
  },
  {
    id: '5',
    slug: 'douyin-xiaohongshu-selfie-style',
    title: '小红书&抖音自拍风格深度对比：哪种更适合你？',
    excerpt: '两大平台自拍风格全对比——从滤镜偏好、构图习惯到内容调性，帮你找到最适合自己风格的社交媒体自拍路线。',
    category: '网红风格',
    categorySlug: 'influencer-style',
    image: 'https://readdy.ai/api/search-image?query=Split%20screen%20comparison%20of%20two%20different%20social%20media%20selfie%20styles%2C%20one%20minimal%20elegant%20one%20vibrant%20trendy%2C%20Asian%20beauty%20portraits%2C%20warm%20lighting%2C%20editorial%20design%2C%20clean%20aesthetic%2C%20cream%20background&width=1200&height=675&seq=article-platform&orientation=landscape',
    author: '菲菲',
    date: '2026-08-01',
    readTime: '9分钟',
    tags: ['小红书', '抖音', '社交平台', '网红风格'],
    featured: false,
    content: [
      { type: 'heading', content: '两大平台的风格差异' },
      { type: 'paragraph', content: '小红书和抖音作为亚洲最火的两大社交平台，各自形成了独特的自拍美学体系。了解它们的风格差异，能够帮助你更有针对性地打造个人自拍风格，在合适的平台获得更多关注。' },
      { type: 'heading', content: '小红书风格：精致生活感' },
      { type: 'paragraph', content: '小红书自拍强调"精致感"和"氛围感"。照片通常经过精心调色，色调偏向柔和的奶油色或莫兰迪色系。构图讲究、细节丰富，常搭配咖啡、花束、书籍等生活化元素，营造出一种"不经意间的精致"。' },
      { type: 'image', content: '', imageUrl: 'https://readdy.ai/api/search-image?query=Xiaohongshu%20style%20lifestyle%20selfie%20with%20coffee%20and%20magazine%20in%20cozy%20cafe%2C%20warm%20cream%20tones%2C%20minimal%20elegant%20aesthetic%2C%20Asian%20woman%20portrait%2C%20soft%20natural%20light%2C%20editorial%20photography%2C%20clean%20background&width=1200&height=675&seq=article-pl-2&orientation=landscape', imageAlt: '小红书风格自拍示范' },
      { type: 'heading', content: '抖音风格：活力潮流感' },
      { type: 'paragraph', content: '抖音自拍则更加注重"话题性"和"潮流感"。滤镜风格更加多元大胆，从复古Y2K到赛博朋克，各种风格都能在抖音找到受众。动态感和趣味性是抖音自拍的两大关键词。' },
      { type: 'tip', content: '平台选择建议：如果你喜欢精致优雅的风格，小红书更适合你；如果你追求潮流和互动，抖音会是更好的选择。当然，在两个平台建立不同的风格体系也是可行的策略。' },
    ],
  },
  {
    id: '6',
    slug: 'makeup-for-selfie',
    title: '上镜妆容全攻略：自拍时妆容应该怎么画？',
    excerpt: '日常妆容和自拍妆容完全不同！掌握这些上镜妆容技巧，让你的五官在镜头前更加立体精致，告别"不上镜"的困扰。',
    category: '美妆自拍',
    categorySlug: 'beauty-selfie',
    image: 'https://readdy.ai/api/search-image?query=Asian%20beauty%20makeup%20tutorial%20flat%20lay%20with%20cosmetics%20brush%20and%20mirror%2C%20warm%20peach%20tones%2C%20soft%20natural%20light%2C%20minimal%20elegant%20composition%2C%20editorial%20beauty%20photography%2C%20cream%20background%2C%20professional%20setup&width=1200&height=675&seq=article-makeup&orientation=landscape',
    author: '小美',
    date: '2026-07-28',
    readTime: '11分钟',
    tags: ['上镜妆容', '美妆教程', '化妆技巧', '自拍妆容'],
    featured: true,
    content: [
      { type: 'heading', content: '为什么日常妆容在自拍里不够好看？' },
      { type: 'paragraph', content: '很多女生都有这样的困惑：镜子里看起来很美的妆容，到了自拍里却显得平淡无奇。这是因为手机摄像头会弱化色彩对比度，同时会放大面部的缺点。上镜妆容需要在日常妆容的基础上做一些关键调整。' },
      { type: 'heading', content: '底妆：光泽感是王道' },
      { type: 'paragraph', content: '在亚洲美妆自拍中，底妆的光泽感至关重要。韩式水光肌之所以在自拍中特别好看，是因为光泽感能够反射光线，让面部轮廓更加立体。建议使用带微珠光的妆前乳，在颧骨、鼻梁和眉骨处重点提亮。' },
      { type: 'image', content: '', imageUrl: 'https://readdy.ai/api/search-image?query=Dewy%20glass%20skin%20Asian%20makeup%20closeup%20portrait%2C%20highlighted%20cheekbones%2C%20natural%20glowing%20complexion%2C%20soft%20peach%20blush%2C%20editorial%20beauty%20photography%2C%20cream%20background%2C%20professional%20lighting&width=1200&height=675&seq=article-mk-2&orientation=landscape', imageAlt: '光泽感底妆自拍效果' },
      { type: 'heading', content: '眼妆：放大双眼的关键' },
      { type: 'paragraph', content: '自拍时眼睛是绝对的视觉焦点。上镜眼妆需要在日常眼妆的基础上更加精细：眼线要拉长微微上挑，睫毛要夹翘并选用浓密型睫毛膏，卧蚕要用浅色珠光提亮。这些细节在自拍中会被放大，效果非常明显。' },
      { type: 'list', content: '上镜妆容必备单品清单', items: ['微珠光妆前乳：打造光泽肌底', '高遮瑕粉底液：均匀肤色是基础', '修容棒：增强面部立体感', '浓密型睫毛膏：放大双眼效果显著', '腮红（蜜桃色系）：提升气色，自拍友好', '哑光唇釉：避免反光，轮廓更清晰'] },
    ],
  },
  {
    id: '7',
    slug: 'selfie-angles-face-shape',
    title: '不同脸型的自拍角度指南：找到你的最佳上镜角度',
    excerpt: '圆脸、方脸、长脸、心形脸——不同脸型需要不同的自拍角度。超详细角度分析，帮每位女生找到属于自己的最佳自拍姿势。',
    category: '拍摄技巧',
    categorySlug: 'shooting-tips',
    image: 'https://readdy.ai/api/search-image?query=Diagram%20showing%20different%20selfie%20angles%20for%20various%20face%20shapes%2C%20Asian%20woman%20demonstrating%20poses%2C%20educational%20beauty%20guide%2C%20warm%20lighting%2C%20minimal%20studio%2C%20clean%20cream%20background%2C%20professional%20tutorial%20style&width=1200&height=675&seq=article-angles&orientation=landscape',
    author: '菲菲',
    date: '2026-07-25',
    readTime: '9分钟',
    tags: ['自拍角度', '脸型分析', '上镜技巧', '姿势指导'],
    featured: false,
    content: [
      { type: 'heading', content: '认识你的脸型' },
      { type: 'paragraph', content: '找到适合自己脸型的自拍角度，是拍出好看自拍的基础。很多女生盲目模仿网红的自拍角度，却发现效果并不理想——这是因为不同脸型需要不同的角度策略。本节将为你详细分析各种脸型的最佳自拍角度。' },
      { type: 'heading', content: '圆脸：拉长是关键' },
      { type: 'paragraph', content: '圆脸女生的核心策略是创造纵向延伸感。推荐采用45度侧脸角度，从略高于眼睛的位置俯拍，手机举到额头高度。这样可以拉长面部轮廓，同时突出下颌线条的柔美感。' },
      { type: 'heading', content: '方脸：柔化下颌线条' },
      { type: 'paragraph', content: '方脸女生的优势在于五官立体，自拍时的策略是柔化下颌的角度感。推荐微微低头、下巴内收的姿势，从正上方或稍高的角度拍摄，让下颌线条看起来更加柔和流畅。' },
      { type: 'tip', content: '通用法则：无论什么脸型，将手机举到与眼睛齐平或略高的位置，都会让自拍效果更佳。避免从下方仰拍，因为那会让任何人看起来都不够理想。' },
      { type: 'list', content: '不同脸型角度速查表', items: ['圆脸：45度侧脸 + 高角度俯拍 + 头发修饰脸颊', '方脸：微低头 + 下巴内收 + 柔和侧光', '长脸：正面偏上 + 刘海修饰额头 + 水平拍摄', '心形脸：正面 + 下巴微抬 + 强调眼部', '鹅蛋脸：几乎所有角度都适合，自由发挥即可'] },
    ],
  },
  {
    id: '8',
    slug: 'selfie-lighting-equipment',
    title: '自拍打光设备入门指南：从零搭建你的家庭自拍灯光',
    excerpt: '不需要专业影棚！用几百元的预算就能搭建一套实用的家庭自拍灯光系统。环形灯、柔光箱、LED补光灯，手把手教你选对设备。',
    category: '设备与灯光',
    categorySlug: 'equipment-lighting',
    image: 'https://readdy.ai/api/search-image?query=Home%20selfie%20lighting%20setup%20with%20ring%20light%20and%20softbox%20on%20minimal%20desk%2C%20warm%20studio%20lighting%2C%20Asian%20content%20creator%20workspace%2C%20clean%20cream%20wall%2C%20professional%20equipment%2C%20cozy%20home%20studio%20vibe&width=1200&height=675&seq=article-lighting&orientation=landscape',
    author: '科技阿杰',
    date: '2026-07-22',
    readTime: '8分钟',
    tags: ['打光设备', '环形灯', '柔光箱', '灯光推荐'],
    featured: false,
    content: [
      { type: 'heading', content: '为什么光线设备如此重要？' },
      { type: 'paragraph', content: '很多自拍新手把所有精力都放在手机和美颜App上，却忽略了最关键的灯光设备。实际上，一套合适的灯光设备能带来的自拍质量提升，远超升级手机本身。好的灯光能让皮肤质感大幅改善，还能塑造出专业级的面部光影。' },
      { type: 'heading', content: '环形灯：自拍神器' },
      { type: 'paragraph', content: '环形灯（Ring Light）是自拍领域最受欢迎的设备之一。它的环形设计能让光线均匀包裹面部，同时产生标志性的环形眼神光，让你的眼睛看起来更加明亮有神。推荐选择直径30cm以上、支持色温和亮度调节的型号。' },
      { type: 'image', content: '', imageUrl: 'https://readdy.ai/api/search-image?query=Ring%20light%20with%20smartphone%20mount%20on%20tripod%2C%20warm%20lighting%20setting%2C%20minimal%20home%20interior%2C%20cream%20wall%2C%20content%20creator%20equipment%2C%20professional%20product%20photography%2C%20clean%20aesthetic&width=1200&height=675&seq=article-lg-2&orientation=landscape', imageAlt: '环形灯自拍设备展示' },
      { type: 'heading', content: '柔光箱与LED补光灯' },
      { type: 'paragraph', content: '如果预算允许，搭配一个柔光箱或LED补光灯可以进一步优化光效。柔光箱能产生更大面积的柔和光线，非常适合拍摄全身自拍或需要大面积均匀照明的场景。LED补光灯则更加便携，适合外出拍摄时使用。' },
      { type: 'list', content: '家庭自拍灯光配置推荐', items: ['入门级（¥200-500）：单环形灯 + 手机支架', '进阶级（¥500-1000）：环形灯 + LED补光灯 + 背景布', '专业级（¥1000-2000）：环形灯 + 双柔光箱 + 可调节灯架'] },
    ],
  },
];

export const getArticleBySlug = (slug: string): Article | undefined => {
  return articles.find((a) => a.slug === slug);
};

export const getArticlesByCategory = (categorySlug: string): Article[] => {
  return articles.filter((a) => a.categorySlug === categorySlug);
};

export const getFeaturedArticles = (): Article[] => {
  return articles.filter((a) => a.featured);
};